import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../context/AuthContext'
import { formatDate } from '../utils/dateFormatter'
import { debounce } from '../utils/debounce'
import './FranchiseDashboard.css'

const TagsFranchiseDashboard = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [tags, setTags] = useState([])
    const [loading, setLoading] = useState(true)
    const [stats, setStats] = useState({
        total: 0,
        assigned: 0,
        available: 0,
        active: 0
    })
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    })
    const [filters, setFilters] = useState({
        is_active: '',
        is_car: '',
        search: ''
    })
    const [searchInput, setSearchInput] = useState('')

    // Memoize filters to prevent unnecessary re-renders
    const memoizedFilters = useMemo(() => filters, [
        filters.is_active,
        filters.is_car,
        filters.search
    ])

    // Debounced search handler
    const debouncedSearch = useMemo(
        () => debounce((value) => {
            setFilters(prev => ({ ...prev, search: value }))
            setPagination(prev => ({ ...prev, page: 1 }))
        }, 500),
        []
    )

    // Handle search input change
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value
        setSearchInput(value)
        debouncedSearch(value)
    }, [debouncedSearch])

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }))
        setPagination(prev => ({ ...prev, page: 1 }))
    }

    const fetchMyTags = useCallback(async (page = 1, currentLimit = pagination.limit) => {
        try {
            setLoading(true)
            const params = new URLSearchParams({
                page,
                limit: currentLimit,
                ...(memoizedFilters.is_active && { is_active: memoizedFilters.is_active }),
                ...(memoizedFilters.is_car && { is_car: memoizedFilters.is_car }),
                ...(memoizedFilters.search && { search: memoizedFilters.search })
            })

            const [tagsRes, statsRes] = await Promise.all([
                api.get(`/qrcodes/franchise/my-tags?${params}`),
                api.get('/qrcodes/franchise/stats')
            ])

            if (tagsRes.data.success) {
                setTags(tagsRes.data.data.qrcodes || [])
                setPagination(tagsRes.data.data.pagination)
            }

            if (statsRes.data.success) {
                setStats(statsRes.data.data.stats)
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error)
        } finally {
            setLoading(false)
        }
    }, [pagination.limit, memoizedFilters])

    useEffect(() => {
        fetchMyTags(pagination.page)
    }, [fetchMyTags, pagination.page])

    const handlePageChange = (newPage) => {
        fetchMyTags(newPage)
    }

    if (loading) {
        return (
            <div className="franchise-dashboard-container">
                <div className="empty-state">
                    <div className="spinner"></div>
                    <p>Loading your tags...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="franchise-dashboard-container">
            <div className="franchise-header">
                <div className="header-content">
                    {user?.logo_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <img
                                src={user.logo_url}
                                alt="Franchise Logo"
                                style={{ width: '50px', height: '50px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.2)' }}
                            />
                            <div>
                                <h1>Tags Dashboard</h1>
                                <p>Franchise: {user?.name}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h1>Tags Dashboard</h1>
                            <p>Franchise: {user?.name}</p>
                        </>
                    )}
                </div>
                <div className="header-actions">
                    <button className="btn-action" onClick={() => navigate('/franchise/tags')}>
                        Manage All Tags
                    </button>
                </div>
            </div>

            <div className="dashboard-metrics">
                <div className="metric-card">
                    <h3>Total Tags</h3>
                    <div className="metric-value">{stats.total}</div>
                    <span className="metric-trend neutral">Your inventory</span>
                </div>
                <div className="metric-card">
                    <h3>Assigned</h3>
                    <div className="metric-value">{stats.assigned}</div>
                    <span className="metric-trend positive">To customers</span>
                </div>
                <div className="metric-card">
                    <h3>Available</h3>
                    <div className="metric-value">{stats.available}</div>
                    <span className="metric-trend neutral">Ready to assign</span>
                </div>
                <div className="metric-card">
                    <h3>Active</h3>
                    <div className="metric-value">{stats.active}</div>
                    <span className="metric-trend positive">Currently active</span>
                </div>
            </div>

            <div className="dashboard-content">
                <div className="section-title">
                    <h2>Your Tags</h2>
                </div>

                {/* Search & Filters */}
                <div className="qr-filters" style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    marginBottom: '1.5rem', 
                    background: 'rgba(255,255,255,0.03)', 
                    padding: '1rem', 
                    borderRadius: '0.8rem',
                    border: '1px solid rgba(255,255,255,0.05)',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                        <input
                            type="text"
                            placeholder="Search by UUID or Vehicle Number..."
                            value={searchInput}
                            onChange={handleSearchChange}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                borderRadius: '0.5rem',
                                background: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: 'white',
                                outline: 'none'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                            value={filters.is_active}
                            onChange={(e) => handleFilterChange('is_active', e.target.value)}
                            style={{
                                padding: '0.75rem 1rem',
                                borderRadius: '0.5rem',
                                background: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                        <select
                            value={filters.is_car}
                            onChange={(e) => handleFilterChange('is_car', e.target.value)}
                            style={{
                                padding: '0.75rem 1rem',
                                borderRadius: '0.5rem',
                                background: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="">All Types</option>
                            <option value="true">Car</option>
                            <option value="false">Bike</option>
                        </select>
                    </div>
                </div>

                {tags.length === 0 ? (
                    <div className="empty-orders">
                        <div>🏷️</div>
                        <p>
                            {filters.search || filters.is_active || filters.is_car 
                                ? 'No tags found matching your filters' 
                                : 'No tags assigned to your franchise yet'}
                        </p>
                    </div>
                ) : (
                    <div className="table-container" style={{ marginTop: '1.5rem' }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Tag UUID</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Assigned To</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tags.map(tag => (
                                    <tr key={tag.id}>
                                        <td>
                                            <code style={{
                                                background: 'rgba(129, 140, 248, 0.1)',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '0.25rem',
                                                fontSize: '0.85rem'
                                            }}>
                                                {tag.code_uuid}
                                            </code>
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '0.5rem',
                                                background: !tag.is_car
                                                    ? 'rgba(59, 130, 246, 0.2)'
                                                    : 'rgba(16, 185, 129, 0.2)',
                                                color: !tag.is_car
                                                    ? '#60a5fa'
                                                    : '#34d399',
                                                fontSize: '0.85rem',
                                                fontWeight: '600'
                                            }}>
                                                {tag.is_car ? 'Car' : 'Bike'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${tag.is_active ? 'ready' : 'pending'}`}>
                                                {tag.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            {tag.linking ? (
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                                        {[tag.linking.first_name, tag.linking.last_name].filter(Boolean).join(' ') || tag.linking.vehicle_number || 'Linked'}
                                                    </span>
                                                    {tag.linking.mobile && (
                                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                            {tag.linking.mobile}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                    In Inventory
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                            {formatDate(tag.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination Controls */}
                <div className="pagination-footer" style={{ 
                    marginTop: '2rem', 
                    paddingTop: '1.5rem', 
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}>
                    <div className="pagination-info" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        Showing <strong>{((pagination.page - 1) * pagination.limit) + 1}</strong> - <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of <strong>{pagination.total}</strong> tags
                    </div>

                    <div className="pagination-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="limit-selector" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select 
                                value={pagination.limit}
                                onChange={(e) => {
                                    const newLimit = parseInt(e.target.value)
                                    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))
                                    // We need to fetch with new limit
                                    fetchMyTags(1, newLimit)
                                }}
                                style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: 'white',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.4rem',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        <div className="page-buttons" style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="btn-pagination"
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    borderRadius: '0.5rem', 
                                    background: pagination.page === 1 ? 'transparent' : 'rgba(255,255,255,0.05)', 
                                    border: '1px solid rgba(255,255,255,0.1)', 
                                    color: pagination.page === 1 ? 'rgba(255,255,255,0.2)' : 'white', 
                                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Previous
                            </button>
                            <span style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '0 1rem',
                                background: 'rgba(var(--primary-rgb), 0.1)',
                                borderRadius: '0.5rem',
                                color: 'var(--primary-color)',
                                fontWeight: '600',
                                fontSize: '0.9rem'
                            }}>
                                {pagination.page} / {pagination.pages || 1}
                            </span>
                            <button
                                className="btn-pagination"
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.pages || pagination.pages === 0}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    borderRadius: '0.5rem', 
                                    background: (pagination.page === pagination.pages || pagination.pages === 0) ? 'transparent' : 'rgba(255,255,255,0.05)', 
                                    border: '1px solid rgba(255,255,255,0.1)', 
                                    color: (pagination.page === pagination.pages || pagination.pages === 0) ? 'rgba(255,255,255,0.2)' : 'white', 
                                    cursor: (pagination.page === pagination.pages || pagination.pages === 0) ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TagsFranchiseDashboard
